import { useCallback, useEffect, useState } from 'react';
import { Box, Button, Link, Tab, Tabs } from '@mui/material';
import CommonListTable, { CommonListColumn } from '../Common/CommonListTable';
import { TransactionServies } from '../../services/TransactionsService';
import { RazorpayOrderReferenceModelEnums } from '../../enums/RazorpayOrderReferenceModelEnums';
import PartialRefundDialog from '../Common/PartialRefundDialog';
import CouponExpiryExtensionListForModule from '../Transactions/CouponExpiryExtensionForHouses';
import moment from 'moment';

interface InputProps {
    houseId: number;
}

const transactionService = new TransactionServies();

function getLink(row: any, modelName: string): string {
    const referenceId = row?.referenceId;
    if (modelName === RazorpayOrderReferenceModelEnums.ONE_MEAL_SERVICE) return `/#/OneMealService/${referenceId}/show`;
    if (modelName === RazorpayOrderReferenceModelEnums.TRIAL_V3) return `/#/Trials/${referenceId}/show`;
    if (modelName === RazorpayOrderReferenceModelEnums.USER_BUNDLE_PAYMENTS) return `/#/UserBundles/${referenceId}/show`;
    if (modelName === RazorpayOrderReferenceModelEnums.DISCOUNT_COUPON_ORDER) return `/#/Combo/${referenceId}/show`;
    return `/#/ChefForParty/${referenceId}/show`;
}

const HousesTransactionTab = ({ houseId }: InputProps) => {
    const [modelName, setModelName] = useState<RazorpayOrderReferenceModelEnums>(RazorpayOrderReferenceModelEnums.USER_BUNDLE_PAYMENTS);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [data, setData] = useState<any[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [selectedRow, setSelectedRow] = useState<any>(null);
    const [partialRefundOpen, setPartialRefundOpen] = useState(false);

    const refreshData = useCallback(() => {
        setLoading(true);
        transactionService
            .getTransactionForHouse(houseId, modelName, rowsPerPage, page * rowsPerPage)
            .then((res) => {
                setData(res?.data ?? []);
                setTotal(res?.total ?? 0);
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [houseId, modelName, page, rowsPerPage]);

    useEffect(() => {
        refreshData();
    }, [refreshData]);

    useEffect(() => {
        setPage(0);
    }, [modelName]);

    const isCouponExpiry = modelName === RazorpayOrderReferenceModelEnums.COUPON_ORDER_EXPIRY_EXTENSION;

    const columns: CommonListColumn[] = [
        { label: 'Id', field: 'id' },
        { label: 'Amount', field: 'amount' },
        { label: 'Currency', field: 'currency' },
        { label: 'Order Id', field: 'razorpayOrderId' },
        { label: 'Refund Amount', field: 'refundAmount' },
        { label: 'State', field: 'state', type: 'status' },
        {
            label: 'Reference Id',
            render: (row) => (
                <Link href={getLink(row, modelName)} underline="hover">
                    {row?.referenceId}
                </Link>
            ),
        },
        { label: 'Razorpay Payment Id', field: 'razorpaySuccessPaymentId' },
        {
            label: 'Payment Date',
            render: (row) => row?.paymentDate ? moment(row?.paymentDate).format('DD-MM-YYYY hh:mm A') : '-',
        },
        {
            label: 'Actions',
            stopRowClick: true,
            render: (row) => {
                const refundable = Number(row?.amount) - Number(row?.refundAmount || 0);
                const canRefund = row?.state === 'PAID' && refundable > 0;
                return (
                    <Button
                        variant="contained"
                        color="error"
                        size="small"
                        disabled={!canRefund}
                        onClick={() => {
                            setSelectedRow(row);
                            setPartialRefundOpen(true);
                        }}
                    >
                        Partial Refund
                    </Button>
                );
            },
        },
    ];

    return (
        <>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={modelName} onChange={(_, v) => setModelName(v)}>
                    <Tab label="Subscription" value={RazorpayOrderReferenceModelEnums.USER_BUNDLE_PAYMENTS} />
                    <Tab label="One Meal" value={RazorpayOrderReferenceModelEnums.ONE_MEAL_SERVICE} />
                    <Tab label="Trial" value={RazorpayOrderReferenceModelEnums.TRIAL_V3} />
                    <Tab label="Coupon" value={RazorpayOrderReferenceModelEnums.DISCOUNT_COUPON_ORDER} />
                    <Tab label="Coupon Extension" value={RazorpayOrderReferenceModelEnums.COUPON_ORDER_EXPIRY_EXTENSION} />
                    <Tab label="Chef for party" value={RazorpayOrderReferenceModelEnums.CHEF_FOR_PARTY} />
                </Tabs>
            </Box>

            {isCouponExpiry ? (
                <CouponExpiryExtensionListForModule data={data} />
            ) : (
                <CommonListTable
                    columns={columns}
                    data={data}
                    loading={loading}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    total={total}
                    onPageChange={setPage}
                    onRowsPerPageChange={setRowsPerPage}
                    emptyText="No Transaction Found"
                />
            )}

            {partialRefundOpen && selectedRow && (
                <PartialRefundDialog
                    open={partialRefundOpen}
                    razorpayOrderId={selectedRow.id}
                    maxRefundable={Number(selectedRow?.amount) - Number(selectedRow?.refundAmount || 0)}
                    onClose={() => setPartialRefundOpen(false)}
                    onSuccess={() => {
                        setPartialRefundOpen(false);
                        setSelectedRow(null);
                        refreshData();
                    }}
                />
            )}
        </>
    );
};

export default HousesTransactionTab;
